/**
 * URL Params Initializer Component
 *
 * Syncs token, transaction_id, API version, and context data from URL parameters to the store
 * once at app initialization. Since these values never change during a transaction, we only
 * need to sync them once. This eliminates the need for duplicate useEffect hooks in every route.
 *
 * API Version Detection:
 * - accesstoken parameter → v5 API (OAuth flow, all params from URL)
 * - token + context params (operation, wallet_provider, asset_code) → v2 API (JWT, context from URL)
 * - token + refNumber → v2 API (JWT, standalone status page pattern)
 * - token without context params or refNumber → v3 API (JWT, context from prepare endpoint)
 *
 * Context Data:
 * - For v2/v5: Context (operation, lang, asset_code, wallet_provider) comes from URL params
 * - For v3: Context comes from PrepareInitializer via /transactions/prepare endpoint
 *
 * Routing:
 * - For v2/v5 with operation=deposit: Redirects to deposit entry page
 */

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
	getApiVersionFromSearchParams,
	validateRampsSearch,
} from "../lib/ramps-search-schema";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

export function URLParamsInitializer() {
	const search = useSearch({ strict: false });
	const navigate = useNavigate();
	const store = useRampsStore();
	const hasInitialized = useRef(false);

	useEffect(() => {
		// Only sync once at app initialization
		if (hasInitialized.current) return;
		hasInitialized.current = true;

		// Sync auth token from URL to store (both v3 and v5 API)
		const tokenFromUrl = search.accesstoken || search.token;
		if (tokenFromUrl && tokenFromUrl !== store.token) {
			rampsActions.setToken(tokenFromUrl);
		}

		// Detect and set API version based on URL parameters
		// - accesstoken → v5 (OAuth)
		// - token + context params (operation, wallet_provider, asset_code) → v2 (JWT + URL context)
		// - token + refNumber → v2 (JWT + standalone status page pattern)
		// - token without context params or refNumber → v3 (JWT + prepare endpoint)
		const validatedSearch = validateRampsSearch(search);
		const detectedVersion = getApiVersionFromSearchParams(validatedSearch);
		if (detectedVersion !== store.apiVersion) {
			rampsActions.setApiVersion(detectedVersion);
			if (import.meta.env.DEV) {
				const versionSource =
					detectedVersion === "v5"
						? "accesstoken (OAuth)"
						: detectedVersion === "v2"
							? search.refNumber
								? "token + refNumber (JWT v2 standalone)"
								: "token + URL context params (JWT v2)"
							: "token only (JWT v3 - will use prepare endpoint)";
				console.log(
					`🔍 API Version detected: ${detectedVersion} from: ${versionSource}`,
				);
			}
		}

		// Sync transaction ID from URL to store (both v3 and v5)
		// For v3: URL param is initial value, prepare endpoint may override
		// For v5: URL param is the authoritative source
		if (
			search.transaction_id &&
			search.transaction_id !== store.transactionId
		) {
			rampsActions.setTransactionId(search.transaction_id);
		}

		// For v2/v5: Set context data from URL parameters
		// For v3: Context will be set by PrepareInitializer from /transactions/prepare
		if (detectedVersion === "v2" || detectedVersion === "v5") {
			rampsActions.setContextData({
				operation: validatedSearch.operation || "withdraw",
				lang: validatedSearch.lang || "en",
				assetCode: validatedSearch.asset_code || "USDC",
				walletProvider: validatedSearch.wallet_provider || "",
			});
			if (import.meta.env.DEV) {
				console.log(
					`📋 Context data set from URL params (${detectedVersion}):`,
					{
						operation: validatedSearch.operation,
						lang: validatedSearch.lang,
						assetCode: validatedSearch.asset_code,
						walletProvider: validatedSearch.wallet_provider,
					},
				);
			}

			// For v2/v5 with operation=deposit: Redirect to deposit entry page
			// This mirrors the redirect logic in PrepareInitializer for v3 API
			if (
				validatedSearch.operation === "deposit" &&
				!window.location.pathname.includes("/deposit")
			) {
				if (import.meta.env.DEV) {
					console.log(
						"🔀 Redirecting to deposit page based on operation param",
					);
				}
				navigate({
					to: "/ramps/deposit",
					search: {
						...validatedSearch,
						step: "deposit",
					},
				});
			}
		}
	}, [search, store.transactionId, store.token, store.apiVersion, navigate]);

	// This is an initialization component - renders nothing
	return null;
}
